/**
 * Bulk price editing: export a sheet, change the price columns, upload it back, review, commit.
 *
 * The upload step is deliberately **not** here — it is `uploadImport` from `imports.ts` with
 * `importType: 'price_update'`. One upload pipeline serves every import type, and a second one for
 * prices would drift from it. What is here is the half that is specific to prices: the export, the
 * changed-rows preview, and the commit.
 *
 * Committing **closes** the old price rows and opens new ones; it never edits a row in place. Every
 * receipt ever printed was printed against one of those rows, so editing one would retroactively
 * change what a customer was charged. That is also why this invalidates `priceListTag` per variant
 * rather than trying to patch anything: the history genuinely got longer.
 */

import { api } from '../api.js';
import { priceBulkTag, stockListTag } from '../tags.js';
import type { CategoryId, ImportBatch, ImportBatchId, ShopId } from '../../types/api.js';
import type { PriceBulkPreview } from '../../types/stockIntegrity.js';

export const priceBulkApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * The editable sheet, as a blob — it needs the auth header, which a plain link cannot send.
     * Every column is locked except the three prices: the SKU is the match key, and editing one
     * does not fail, it silently retargets the row at a different product.
     */
    exportPriceSheet: build.query<
      Blob,
      { categoryId?: CategoryId | undefined; shopId?: ShopId | undefined }
    >({
      query: (params) => ({
        url: '/prices/bulk/export',
        params,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    /**
     * "Raise every phone case by 50": the server moves one price column of the export by a fixed
     * amount and stages it exactly as an uploaded sheet, so the review and commit are the same.
     * `amountKes` is signed — negative lowers.
     */
    adjustPrices: build.mutation<
      ImportBatch,
      {
        categoryId?: CategoryId | undefined;
        shopId?: ShopId | undefined;
        priceType: 'retail' | 'wholesale';
        amountKes: number;
      }
    >({
      query: (body) => ({ url: '/prices/bulk/adjust', method: 'POST', body }),
    }),

    /** Only the rows that change, old value beside new. The rejections are in the generic row list. */
    getPriceBulkPreview: build.query<PriceBulkPreview, ImportBatchId>({
      query: (batchId) => `/prices/bulk/${batchId}/preview`,
      providesTags: (_result, _error, batchId) => [priceBulkTag(batchId)],
      // Recomputed against live prices server-side, so a stale copy would describe a commit that
      // will not happen.
      keepUnusedDataFor: 0,
    }),

    /**
     * `acknowledgedSkus` are the rows whose large moves somebody has actually read. Per row rather
     * than one blanket flag, because a single tick is the control that gets clicked without looking.
     */
    commitPriceBulk: build.mutation<
      ImportBatch,
      { batchId: ImportBatchId; acknowledgedSkus?: readonly string[] | undefined }
    >({
      query: ({ batchId, acknowledgedSkus }) => ({
        url: `/prices/bulk/${batchId}/commit`,
        method: 'POST',
        body: { acknowledgedSkus: acknowledgedSkus ?? [] },
      }),
      // `Price` is keyed per variant and a commit can touch hundreds, so the whole Price type is
      // invalidated rather than enumerating them. The stock list carries prices on its rows too.
      invalidatesTags: (_result, _error, { batchId }) => [
        priceBulkTag(batchId),
        'Price',
        stockListTag(),
      ],
    }),
  }),
});

export const {
  useLazyExportPriceSheetQuery,
  useGetPriceBulkPreviewQuery,
  useCommitPriceBulkMutation,
  useAdjustPricesMutation,
} = priceBulkApi;
